import type {
  Character,
  EasyModeProgress,
  EasyModeStep,
  EasyModeStepId,
  FilmProject,
  ImageQualitySetting,
  ImageSizeSetting,
  Scene,
  SeedanceVoicePreset,
  UploadedAssetManifest,
  UploadedAssetRecord
} from "../types";

export interface GeneratedCardResult {
  name?: string;
  characterId?: string;
  cardStatus?: string;
  cardAssetLabel?: string;
  imageUrl?: string;
  sourceUrl?: string;
  storageKey?: string;
  assetId?: string;
  fileName?: string;
}

/** A pixel reference fed into image conditioning: a character card (by name) or scene plate (by sceneId). */
export interface ReferenceAsset {
  kind: "characterCard" | "sceneReference";
  characterName?: string;
  sceneId?: string;
  storageKey?: string | null;
  sourceUrl?: string;
}

export interface GeneratedScenePlate {
  sceneId?: string;
  sceneLabel?: string;
  label?: string;
  referenceStatus?: string;
  assetId?: string;
  fileName?: string;
  storageKey?: string;
  sourceUrl?: string;
  imageUrl?: string;
  cloudStatus?: string;
  cloudMessage?: string;
}

const DEFAULT_VOICE_PRESET: SeedanceVoicePreset = "cinematic_natural";

function normalizeKey(value: string | undefined): string {
  return (value || "").trim().toLowerCase();
}

/**
 * Easy Mode auto-assigns a Seedance voice preset to every character that still needs a voice, so the
 * package is render-ready for EvoLink. Locked, disabled, or already-assigned voices are left as-is.
 */
export function planEasyModeVoices(characters: Character[]): Character[] {
  return characters.map((character) => {
    if (character.voiceLocked || character.voiceDisabled || character.voiceStatus === "assigned") {
      return character;
    }
    return {
      ...character,
      voiceStatus: "assigned",
      voiceSource: "seed_generated",
      voicePreset: character.voicePreset || DEFAULT_VOICE_PRESET
    };
  });
}

/**
 * Map generated card results back onto characters. Uploads and locks always win — a protected card is
 * never overwritten — matching the product's strict source-of-truth precedence.
 */
export function applyGeneratedCardsToCharacters(characters: Character[], cards: GeneratedCardResult[]): Character[] {
  const byId = new Map<string, GeneratedCardResult>();
  const byName = new Map<string, GeneratedCardResult>();
  for (const card of cards) {
    if (String(card.cardStatus || "") !== "generated") continue;
    if (card.characterId) byId.set(card.characterId, card);
    if (card.name) byName.set(normalizeKey(card.name), card);
  }
  return characters.map((character) => {
    if (character.cardLocked || character.cardStatus === "uploaded") return character;
    const card = byId.get(character.id) || byName.get(normalizeKey(character.name));
    if (!card) return character;
    return {
      ...character,
      cardStatus: "generated",
      cardSource: "seed_generated",
      cardAssetLabel: card.cardAssetLabel || `${character.name} card (generated)`
    };
  });
}

/**
 * Merge generated environment plates into the sceneReferences bucket. A scene that already has an
 * uploaded reference keeps it (uploads are the source of truth) — generated plates only fill scenes
 * that have no reference yet, and only when the plate actually persisted (has a storage key or src).
 */
export function applyGeneratedScenePlates(
  existing: UploadedAssetRecord[],
  plates: GeneratedScenePlate[]
): UploadedAssetRecord[] {
  const claimedSceneIds = new Set(
    existing.map((record) => normalizeKey(record.sceneId)).filter((value) => value.length > 0)
  );
  const additions: UploadedAssetRecord[] = [];
  for (const plate of plates) {
    if (String(plate.referenceStatus || "") !== "generated") continue;
    const sceneKey = normalizeKey(plate.sceneId);
    if (sceneKey && claimedSceneIds.has(sceneKey)) continue;
    const src = plate.sourceUrl || plate.imageUrl || "";
    if (!plate.storageKey && !src) continue;
    if (sceneKey) claimedSceneIds.add(sceneKey);
    additions.push({
      assetId: plate.assetId || undefined,
      kind: "sceneReference",
      label: plate.label || plate.sceneLabel || "Generated scene plate",
      fileName: plate.fileName || "",
      sourceUrl: src,
      storageKey: plate.storageKey ?? null,
      sceneId: plate.sceneId || undefined,
      sourceOfTruth: false,
      locked: false,
      disabled: false,
      sourceMode: "seed_generated",
      provider: "openai",
      cloudStatus: plate.cloudStatus || "uploaded",
      cloudMessage: plate.cloudMessage || "Generated scene reference plate."
    });
  }
  return additions.length ? [...existing, ...additions] : existing;
}

/**
 * Register generated character cards into the characterCards bucket so they're available as conditioning
 * references and visible in the asset library. Uploaded cards (matched by name) always win.
 */
export function applyGeneratedCardsToManifest(
  existing: UploadedAssetRecord[],
  cards: GeneratedCardResult[]
): UploadedAssetRecord[] {
  const claimedNames = new Set(
    existing.map((record) => normalizeKey(record.characterName || record.label)).filter((value) => value.length > 0)
  );
  const additions: UploadedAssetRecord[] = [];
  for (const card of cards) {
    if (String(card.cardStatus || "") !== "generated") continue;
    const nameKey = normalizeKey(card.name);
    if (nameKey && claimedNames.has(nameKey)) continue;
    const src = card.sourceUrl || card.imageUrl || "";
    if (!card.storageKey && !src) continue;
    if (nameKey) claimedNames.add(nameKey);
    additions.push({
      assetId: card.assetId || undefined,
      kind: "characterCard",
      label: card.cardAssetLabel || `${card.name || "Character"} card`,
      fileName: card.fileName || "",
      sourceUrl: src,
      storageKey: card.storageKey ?? null,
      characterName: card.name || undefined,
      sourceOfTruth: false,
      locked: false,
      disabled: false,
      sourceMode: "seed_generated",
      provider: "openai",
      cloudStatus: "uploaded",
      cloudMessage: "Generated character card."
    });
  }
  return additions.length ? [...existing, ...additions] : existing;
}

/** Drop duplicate references (same character or scene) and any with no resolvable image, keeping the first. */
export function dedupeReferenceAssets(refs: ReferenceAsset[]): ReferenceAsset[] {
  const seen = new Set<string>();
  const out: ReferenceAsset[] = [];
  for (const ref of refs) {
    if (!ref.storageKey && !ref.sourceUrl) continue;
    const id = ref.kind === "characterCard" ? `c:${normalizeKey(ref.characterName)}` : `s:${normalizeKey(ref.sceneId)}`;
    if (id === "c:" || id === "s:" || seen.has(id)) continue;
    seen.add(id);
    out.push(ref);
  }
  return out;
}

export const IMAGE_QUALITY_OPTIONS: Array<{ id: ImageQualitySetting; label: string; hint: string }> = [
  { id: "low", label: "Draft", hint: "≈ 25–35s per image · cheapest test pass" },
  { id: "medium", label: "Standard", hint: "Balanced fidelity for review cuts" },
  { id: "high", label: "High", hint: "Slowest + priciest · final passes only" }
];

export const IMAGE_SIZE_OPTIONS: Array<{ id: ImageSizeSetting; label: string }> = [
  { id: "auto", label: "Match aspect ratio" },
  { id: "1024x1024", label: "Square · 1024×1024" },
  { id: "1536x1024", label: "Wide · 1536×1024" },
  { id: "1024x1536", label: "Tall · 1024×1536" }
];

/**
 * Resolve the concrete OpenAI image size. "auto" follows the project frame: landscape ratios get the
 * wide plate, vertical ratios the tall one, square stays square.
 */
export function resolveImageSize(size: ImageSizeSetting | undefined, aspectRatio: FilmProject["aspectRatio"]): string {
  if (size && size !== "auto") return size;
  if (aspectRatio === "9:16" || aspectRatio === "4:5") return "1024x1536";
  if (aspectRatio === "1:1") return "1024x1024";
  return "1536x1024";
}

/** Scenes targeted by an Easy Mode run: the whole script, or a single scene when sceneScope is its id. */
export function scenesForEasyModeScope(scenes: Scene[], sceneScope: "all" | string): Scene[] {
  if (sceneScope === "all") return scenes;
  const scoped = scenes.filter((scene) => scene.id === sceneScope);
  return scoped.length ? scoped : scenes;
}

/** Characters that appear in the scoped scenes (matched by name), so a one-scene run only generates that scene's cast. */
export function charactersInScenes(characters: Character[], scenes: Scene[]): Character[] {
  const names = new Set(scenes.flatMap((scene) => scene.characters.map((name) => normalizeKey(name))));
  return characters.filter((character) => names.has(normalizeKey(character.name)));
}

const EASY_MODE_STEP_LABELS: Record<EasyModeStepId, string> = {
  voices: "Voices assigned",
  cards: "Character cards",
  plates: "Scene plates",
  storyboard: "Storyboard frames"
};

/** Stage weights for the overall meter: voices are instant; the three image stages share the rest. */
const EASY_MODE_STEP_WEIGHTS: Record<EasyModeStepId, number> = {
  voices: 10,
  cards: 30,
  plates: 30,
  storyboard: 30
};

export function createEasyModeProgress(scopeLabel: string, details?: Partial<Record<EasyModeStepId, string>>): EasyModeProgress {
  const steps: EasyModeStep[] = (Object.keys(EASY_MODE_STEP_LABELS) as EasyModeStepId[]).map((id) => ({
    id,
    label: EASY_MODE_STEP_LABELS[id],
    status: "pending",
    detail: details?.[id] || ""
  }));
  return { startedAt: Date.now(), scopeLabel, steps, warnings: [] };
}

export function patchEasyModeStep(
  progress: EasyModeProgress,
  id: EasyModeStepId,
  patch: Partial<Pick<EasyModeStep, "status" | "detail">>
): EasyModeProgress {
  return {
    ...progress,
    steps: progress.steps.map((step) => (step.id === id ? { ...step, ...patch } : step))
  };
}

export function finishEasyModeProgress(progress: EasyModeProgress, warnings: string[]): EasyModeProgress {
  return { ...progress, finishedAt: Date.now(), warnings: [...progress.warnings, ...warnings] };
}

/** Overall 0–100 meter: full weight for finished steps, half credit for the step in flight. */
export function easyModeProgressPercent(progress: EasyModeProgress): number {
  let earned = 0;
  let total = 0;
  for (const step of progress.steps) {
    const weight = EASY_MODE_STEP_WEIGHTS[step.id] ?? 0;
    total += weight;
    if (step.status === "done" || step.status === "warning" || step.status === "failed") earned += weight;
    else if (step.status === "running") earned += weight / 2;
  }
  if (!total) return 0;
  if (progress.finishedAt) return 100;
  return Math.round((earned / total) * 100);
}

/** Flatten the uploaded-asset manifest into the reference list the storyboard image conditioner expects. */
export function referenceAssetsFromManifest(manifest?: UploadedAssetManifest): ReferenceAsset[] {
  if (!manifest) return [];
  const cards: ReferenceAsset[] = (manifest.characterCards || [])
    .filter((asset) => !asset.disabled && (asset.storageKey || asset.sourceUrl))
    .map((asset) => ({
      kind: "characterCard" as const,
      characterName: asset.characterName || asset.label,
      storageKey: asset.storageKey,
      sourceUrl: asset.sourceUrl
    }));
  const plates: ReferenceAsset[] = (manifest.sceneReferences || [])
    .filter((asset) => !asset.disabled && asset.sceneId && (asset.storageKey || asset.sourceUrl))
    .map((asset) => ({
      kind: "sceneReference" as const,
      sceneId: asset.sceneId,
      storageKey: asset.storageKey,
      sourceUrl: asset.sourceUrl
    }));
  return [...cards, ...plates];
}
